import apiService, { PoliticalMention } from "../services/api";

export type { PoliticalMention };

export const fetchAllPoliticalStatistics = async (params: {
  entityType: "candidate" | "topic";
  from: string;
  to: string;
  includeRadio?: boolean;
}): Promise<PoliticalMention[]> => {
  return apiService.fetchAllPoliticalStatistics(params);
};

export const fetchPoliticalPodcastsCount = async (params: {
  from: string;
  to: string;
  includeRadio?: boolean;
}): Promise<{
  count: number;
  episodeCount: number;
  podcastCount: number;
  mentionsCount: number;
  totalHours: number;
}> => {
  return apiService.fetchPoliticalPodcastsCount(params);
};

export const fetchDailyPoliticalMentions = async (params: {
  from: string;
  to: string;
  includeRadio?: boolean;
  entityType: "candidate" | "topic";
  period?: "daily" | "weekly";
}): Promise<
  Array<{
    date: string;
    [entity: string]: any;
  }>
> => {
  return apiService.fetchDailyPoliticalMentions(params);
};

export const fetchCandidateMentions = async (params: {
  candidateName: string;
  from: string;
  to: string;
  includeRadio?: boolean;
  page?: number;
  limit?: number;
}): Promise<{
  mentions: Array<{
    _id: string;
    mentionText: string;
    context: string;
    segmentStart: number;
    segmentEnd: number;
    duration: number;
    confidence: number;
    subtopics: string[];
    primaryTopicName: string;
    publishedAt: string;
    episodeTitle: string;
    episodeUrl: string;
    podcastName: string;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}> => {
  return apiService.fetchCandidateMentions(params);
};

export interface TopicCandidateBreakdown {
  topics: Array<{
    topic: string;
    totalMentions: number;
    candidates: {
      [candidateName: string]: {
        count: number;
        percentage: number;
      };
    };
  }>;
  candidates: string[];
}

export const fetchTopicCandidateBreakdown = async (params: {
  from: string;
  to: string;
  includeRadio?: boolean;
}): Promise<TopicCandidateBreakdown> => {
  return apiService.fetchTopicCandidateBreakdown(params);
};

export interface CandidateTopicOverTime {
  data: Array<{
    date: string;
    total: number;
    [topicName: string]: number | string;
  }>;
  topics: string[];
  candidate: string;
}

export const fetchCandidateTopicOverTime = async (params: {
  from: string;
  to: string;
  candidate: string;
  period?: "daily" | "weekly";
  includeRadio?: boolean;
}): Promise<CandidateTopicOverTime> => {
  return apiService.fetchCandidateTopicOverTime(params);
};

export interface PodcastMediaKitDemoCompany {
  name: string;
  imageS3Key?: string | null;
  /** Used when no S3 key (e.g. demo placeholder URL). */
  imageUrl?: string | null;
}

export interface YoutubeGeoCountryRow {
  code: string;
  views: number;
  estimatedMinutesWatched: number;
  sharePercent: number;
}

export interface YoutubeGeoCityRow {
  name: string;
  views: number;
  estimatedMinutesWatched: number;
  sharePercent: number;
}

export interface YoutubeAudienceAnalytics {
  gender?: Array<{ type: string; confidence: number }>;
  ageRanges?: Array<{ range: string; confidence: number }>;
  countries?: YoutubeGeoCountryRow[];
  cities?: YoutubeGeoCityRow[];
  oauthPeriod?: { start: string; end: string };
}

/** Metrics taken from each item's snapshot at day N after publish (instead of lifetime). */
export interface MediaKitMetricsWindow {
  enabled: boolean;
  longFormDays: number;
  shortsDays: number;
}

export const MEDIA_KIT_METRICS_WINDOW_DEFAULTS: MediaKitMetricsWindow = {
  enabled: false,
  longFormDays: 14,
  shortsDays: 2,
};

export const MEDIA_KIT_METRICS_WINDOW_MAX_DAYS = 30;

export interface PodcastMediaKit {
  /** Example media kit from GET /api/podcasts/media-kit/example. */
  isDemo?: boolean;
  demoNoticeEs?: string;
  demoCompany?: PodcastMediaKitDemoCompany;
  /** Present when the kit is scoped to a single program (per-program public URL). */
  program?: {
    _id: string;
    slug: string;
    name: string;
    /** Always present on program-scoped kits (null if no program artwork). */
    imageS3Key: string | null;
  };
  podcast: {
    _id: string;
    /** Always present (null if no uploaded cover in S3). */
    imageS3Key: string | null;
    title: string;
    author: string;
    description: string;
    imageUrl: string;
    slug: string;
  };
  statistics: {
    episodeCount: number;
    totalViews: number;
    totalLikes: number;
    totalComments: number;
    youtubeSubscribers: number;
    spotifyFollowers: number;
    /** From `podcast.socialMetrics` (Instagram Business). */
    instagramFollowers?: number;
    /** From `podcast.socialMetrics` (TikTok). */
    tiktokFollowers?: number;
  };
  platforms: {
    spotify: string | null;
    youtube: string | null;
    instagram: string | null;
    tiktok: string | null;
    website: string | null;
  };
  recentEpisodes: Array<{
    title: string;
    publishedAt: string;
    thumbnail: string | null;
    url: string;
    views: string;
    likes: string;
    comments: string;
  }>;
  mediaKit: {
    enabled: boolean;
    enabledMetrics?: {
      youtube?: {
        longForm?: {
          showContentCount?: boolean;
          showPdfVideoTable?: boolean;
          [key: string]: unknown;
        };
        shorts?: { showContentCount?: boolean; [key: string]: unknown };
        [key: string]: unknown;
      };
      instagram?: { showContentCount?: boolean; [key: string]: unknown };
      facebook?: { showContentCount?: boolean; [key: string]: unknown };
      tiktok?: { showContentCount?: boolean; [key: string]: unknown };
      podscope?: { showContentCount?: boolean; [key: string]: unknown };
    };
    metricsWindow?: MediaKitMetricsWindow;
  };
  selectedMonths: string[];
  analytics?: {
    youtube?: {
      /** @deprecated Prefer `longForm` / `shorts`; kept for older API responses. */
      consumption?: {
        totalViews?: number;
        /** Catalog row count (long-form vs shorts split). */
        contentCount?: number;
        avgViewsPerContent?: number;
        totalInteractions?: number;
        avgInteractions?: number;
      };
      /** @deprecated Prefer `longForm.engagement`. */
      engagement?: { engagementRate?: number };
      /** @deprecated Prefer `longForm.audience`. */
      audience?: YoutubeAudienceAnalytics;
      longForm?: {
        consumption?: {
          totalViews?: number;
          /** Catalog long-form row count in date range. */
          contentCount?: number;
          avgViewsPerContent?: number;
          totalInteractions?: number;
          avgInteractions?: number;
          inProgressCount?: number;
        };
        engagement?: { engagementRate?: number };
        audience?: YoutubeAudienceAnalytics;
      };
      shorts?: {
        consumption?: {
          totalViews?: number;
          /** Catalog Shorts row count in date range. */
          contentCount?: number;
          avgViewsPerContent?: number;
          totalInteractions?: number;
          avgInteractions?: number;
          inProgressCount?: number;
        };
        engagement?: { engagementRate?: number };
        audience?: YoutubeAudienceAnalytics;
      };
    };
    instagram?: {
      reach?: {
        totalReach?: number;
        avgReach?: number;
        totalViews?: number;
        /** Catalog row count in range (when from aggregateInstagramCatalogConsumption). */
        contentCount?: number;
        avgViews?: number;
        totalInteractions?: number;
        avgInteractions?: number;
        reelsReach?: number;
        postsReach?: number;
        reelsViews?: number;
        postsViews?: number;
        reelsCount?: number;
        postsCount?: number;
        reelsInteractions?: number;
        postsInteractions?: number;
        avgReachPerReel?: number;
        avgReachPerPost?: number;
        avgViewsPerReel?: number;
        avgViewsPerPost?: number;
        inProgressCount?: number;
      };
      engagement?: { engagementRate?: number };
      audience?: {
        gender?: Array<{ type: string; percentage: number }>;
        ageRanges?: Array<{ range: string; percentage: number }>;
      };
    };
    tiktok?: {
      consumption?: {
        totalViews?: number;
        /** Catalog row count in range when from aggregate. */
        contentCount?: number;
        avgViewsPerContent?: number;
        totalInteractions?: number;
        avgInteractions?: number;
        /** Items younger than the metrics window (latest value used). */
        inProgressCount?: number;
      };
      engagement?: { engagementRate?: number };
      audience?: {
        selfReported?: boolean;
        gender?: Array<{ type: string; percentage: number }>;
        ageRanges?: Array<{ range: string; percentage: number }>;
      };
    };
    facebook?: {
      consumption?: {
        totalViews?: number;
        contentCount?: number;
        avgViewsPerContent?: number;
        totalInteractions?: number;
        avgInteractions?: number;
      };
      engagement?: { engagementRate?: number };
    };
    podscope?: {
      /** @deprecated Prefer `longForm` / `shorts` consumption. */
      consumption?: {
        totalViews?: number;
        avgViews?: number;
        totalInteractions?: number;
        avgInteractions?: number;
      };
      longForm?: {
        consumption?: {
          totalViews?: number;
          avgViews?: number;
          totalInteractions?: number;
          avgInteractions?: number;
          inProgressCount?: number;
        };
      };
      shorts?: {
        consumption?: {
          totalViews?: number;
          avgViews?: number;
          totalInteractions?: number;
          avgInteractions?: number;
          inProgressCount?: number;
        };
      };
      streaming?: {
        avgViewers?: number;
        peakViewers?: number;
        chatMessages?: number;
        chatMessagesAvg?: number;
        uniqueChatUsersAvg?: number;
      };
      /** Live metrics from `livestream_snapshots` (program-linked streams). */
      streamingFromSnapshots?: {
        avgViewers?: number;
        peakViewers?: number;
        avgChatMessages?: number;
        avgChatUniqueUsers?: number;
        streamCount?: number;
        streams?: Array<{
          streamId: string;
          videoId: string;
          title: string;
          startedAt: string | null;
          avgViewers: number;
          peakViewers: number;
          chatMessages: number;
          chatUniqueUsers: number;
          streamType?: "live" | "premiere" | "upcoming";
        }>;
      };
      /** Podscope-wide program rank for the active scope (lower is better). */
      programRank?: number;
    };
  };
  /** Company that owns this media kit (present on /:companySlug/:podcastSlug/:programSlug routes). */
  company?: { name: string; imageS3Key: string | null };
  /** Paid brand logos grouped by Podscope program (content.programId). */
  paidBrandsByProgram?: Array<{
    programId: string | null;
    programName: string;
    programSlug?: string | null;
    brands: Array<{
      _id: string;
      name: string;
      logoUrl?: string;
      s3Key?: string;
    }>;
  }>;
}

export const fetchPodcastMediaKit = async (
  slug: string,
  companySlug?: string,
): Promise<PodcastMediaKit> => {
  return apiService.getPodcastMediaKit(slug, companySlug);
};

export const fetchPodcastMediaKitExample = async (): Promise<PodcastMediaKit> => {
  return apiService.getMediaKitExample();
};

export const fetchProgramMediaKit = async (
  programSlug: string,
): Promise<PodcastMediaKit> => {
  return apiService.getProgramMediaKit(programSlug);
};

export const fetchProgramMediaKitByPath = async (
  companySlug: string,
  podcastSlug: string,
  programSlug: string,
): Promise<PodcastMediaKit> => {
  return apiService.getProgramMediaKitByPath(
    companySlug,
    podcastSlug,
    programSlug,
  );
};
